import {
  ButtonContainer,
  DoubleFieldWrapper,
  EyeWrapper,
  FieldWrapper,
  PasswordInputWrapper,
  StyledForm,
} from './CreatePage.styled';
import Wrapper from '../Wrapper/Wrapper';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { usePageTitle } from 'utils/hooks/usePageTitle';
import Icon from 'components/Icon/Icon';
import Button from 'components/Button/Button';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';
import { useNavigate } from 'react-router-dom';
import { RoutesConfig } from 'navigation/routes';
import { Field, Formik, FormikProps } from 'formik';
import { Input } from 'components/Input/InputFormik';
import { createNewAccountValidationSchema } from './validation';
import PageNotFound from 'pages/PageNotFound/PageNotFound';
import { useCreateNewAccount } from './hooks';
import { saveActivationToken } from 'services/ActivationTokenService';

const CreatePage = () => {
  const formikRef = useRef<FormikProps<any> | any>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isPhone } = useBreakpointFlag();
  const [hidePassword, setHidePassword] = useState<boolean>(true);
  const [hideRepeatPassword, setHideRepeatPassword] = useState<boolean>(true);

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const lang = searchParams.get('lang');
  const companyName = searchParams.get('companyName');

  usePageTitle('Create New Account');

  const handlePasswordEyeClick = () => {
    setHidePassword(!hidePassword);
  };

  const handleRepeatPasswordEyeClick = () => {
    setHideRepeatPassword(!hideRepeatPassword);
  };

  if (!token) {
    return <PageNotFound />;
  }

  const { mutate: createNewAccount, isLoading } = useCreateNewAccount();

  const handleSubmit = () => {
    const { email, firstName, lastName, password } = formikRef.current.values;
    createNewAccount(
      { token, firstName, lastName, email, password },
      {
        onSuccess: (data) => {
          saveActivationToken(data.activation_token);
          navigate(
            RoutesConfig.CompanyLoginDetails.buildPath(
              token,
              lang || 'en',
              companyName || '',
              data.user_id
            )
          );
        },
      }
    );
  };

  return (
    <Wrapper title={t('Create New Account')}>
      <Formik
        innerRef={formikRef}
        initialValues={{
          email: '',
          firstName: '',
          lastName: '',
          password: '',
          repeatPassword: '',
        }}
        validationSchema={createNewAccountValidationSchema}
        validateOnChange={true}
        validateOnMount={true}
        validateOnBlur={true}
        onSubmit={() => {
          //
        }}
      >
        {({ setFieldValue, values, isValid, errors, touched }) => {
          return (
            <>
              <StyledForm>
                <DoubleFieldWrapper>
                  <Field
                    name="firstName"
                    as={Input}
                    height={'55rem'}
                    placeholder={t('First name')}
                    onChange={(str: string) => setFieldValue('firstName', str)}
                    wrapperStyles={{ width: '100%' }}
                    errorMessage={
                      touched['firstName'] && errors['firstName']
                        ? errors['firstName']
                        : ''
                    }
                  />
                  <Field
                    name="lastName"
                    as={Input}
                    height={'55rem'}
                    placeholder={t('Last name')}
                    onChange={(str: string) => setFieldValue('lastName', str)}
                    wrapperStyles={{ width: '100%' }}
                    errorMessage={
                      touched['lastName'] && errors['lastName']
                        ? errors['lastName']
                        : ''
                    }
                  />
                </DoubleFieldWrapper>
                <FieldWrapper>
                  <Field
                    name="email"
                    as={Input}
                    height={'55rem'}
                    placeholder={t('E-mail')}
                    onChange={(str: string) => setFieldValue('email', str)}
                    wrapperStyles={{ width: '100%' }}
                    errorMessage={
                      touched['email'] && errors['email'] ? errors['email'] : ''
                    }
                  />
                </FieldWrapper>
                <PasswordInputWrapper>
                  <Field
                    name="password"
                    as={Input}
                    height={'55rem'}
                    placeholder={t('Password')}
                    type={hidePassword ? 'password' : ''}
                    onChange={(str: string) => setFieldValue('password', str)}
                    wrapperStyles={{ width: '100%' }}
                    errorMessage={
                      touched['password'] && errors['password']
                        ? errors['password']
                        : ''
                    }
                  />
                  <EyeWrapper onClick={() => handlePasswordEyeClick()}>
                    <Icon
                      svg={hidePassword ? Eye : EyeSlash}
                      color={COLORS.BLACK}
                      size={20}
                    />
                  </EyeWrapper>
                </PasswordInputWrapper>
                <PasswordInputWrapper>
                  <Field
                    name="repeatPassword"
                    as={Input}
                    height={'55rem'}
                    placeholder={t('Repeat password')}
                    type={hideRepeatPassword ? 'password' : ''}
                    onChange={(str: string) =>
                      setFieldValue('repeatPassword', str)
                    }
                    wrapperStyles={{ width: '100%' }}
                    errorMessage={
                      touched['repeatPassword'] && errors['repeatPassword']
                        ? t(errors['repeatPassword'])
                        : ''
                    }
                  />
                  <EyeWrapper onClick={() => handleRepeatPasswordEyeClick()}>
                    <Icon
                      svg={hideRepeatPassword ? Eye : EyeSlash}
                      color={COLORS.BLACK}
                      size={20}
                    />
                  </EyeWrapper>
                </PasswordInputWrapper>
              </StyledForm>

              <ButtonContainer>
                <Button
                  label={t('Create')}
                  primary
                  disabled={
                    isLoading ||
                    !isValid ||
                    values.password !== values.repeatPassword
                  }
                  width={isPhone ? '100%' : '580rem'}
                  height={'55rem'}
                  onClick={() => handleSubmit()}
                />
              </ButtonContainer>
            </>
          );
        }}
      </Formik>
    </Wrapper>
  );
};

export default CreatePage;
